import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Grid, IconButton, Modal, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { deleteUser, getUserProfile, updateProfilePhoto } from "../../../../../api/apiCall";
import { useAuth } from "../../../../../auth/auth";
import { toast } from "react-toastify";
import EditIcon from '@mui/icons-material/Edit';
import AccessForbidden from "../../../../../components/Access/AccessForbidden";
import { style } from "../../style";
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import Loading from "../../../../../components/Loading/Loading";

const ProfileAdmin = () => {
    const {state} = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [username, setUsername] = useState('');
    const [profileInfo, setProfileInfo] = useState<any>();
    const [searchParams, ] = useSearchParams();
    const [image, setImage] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    const handleImageUpload = async (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const response = await updateProfilePhoto(state.user?.token, profileInfo.id, file);
        if(response) {
            toast.success('Profile updated successfully');
        }
        setImage(file);
      }
    };

    const handleDeleteUser = async () => {
        if(username === profileInfo?.username) {
            const response = await deleteUser(state.user?.token, username, profileInfo?.id);
            if(response) {
                toast.error("Deleted user successfully!");
                navigate(`/pace-team/resourcing/dashboard`);
            }
        }
    };

    const handleEdit = (userId: any) => {
        const url = `/pace-team/resourcing/dashboard/edit-user?userId=${userId}`;
        window.open(url, '_blank');
    };

    const getWorkerInfo = async () => {
        try {
            const response: any = await getUserProfile(state.user?.token, searchParams.get('userId'));
            if(response?.response?.status === 403) {
                setIsAdmin(true);
            } else {
                setProfileInfo(response);
            }
            setLoading(false);
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    useEffect(() => {
        getWorkerInfo();
    }, []);

    if(loading) {
        return <Loading />
    }

    if(isAdmin) {
        return <AccessForbidden />;
    }

    return (
        <>
            <Box
                sx={{
                    mt: 7,
                    gap: 2,
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "1fr 1fr 1fr 1fr"},
                    backgroundColor: "#fff",
                    padding: "50px",
                    margin: "auto",
                    background: "#fff",
                    marginTop: 5
                }}>
                <Grid>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 4 }}>
                        Worker's Details
                    </Typography>
                    <Box
                        sx={{
                            position: "relative",
                            display: "inline-block"
                        }}>
                        <Avatar
                            sx={{ width: 160, height: 160 }}
                            src={image ? URL.createObjectURL(image) : ("data:image/png;base64," + profileInfo?.picture)}
                        />
                        <IconButton
                            component="label"
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                bgcolor: "background.paper",
                                borderRadius: "50%",
                                padding: "5px",
                            }}
                        >
                            <EditIcon />
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleImageUpload}
                            />
                        </IconButton>
                    </Box>
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="body1">
                            <strong>Username</strong> {profileInfo?.username}
                            {profileInfo?.verified ?
                                <GppGoodOutlinedIcon sx={{color: 'green'}} fontSize="small" /> : 
                                " (Not Verified)"
                            }
                        </Typography>
                        <Button
                            variant="contained"
                            sx={{boxShadow: 'none', width: '100px', marginTop: 4, border: "none"}}
                            onClick={() => handleEdit(profileInfo?.id)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outlined"
                            style={{backgroundColor: 'red', color: 'white', border: 'none'}}
                            sx={{width: '100px', marginTop: 4, ml: 2}}
                            onClick={() => setOpen(true)}
                        >
                            Delete
                        </Button>
                    </Box>
                </Grid>
                <Grid size="grow">
                    <Typography
                        variant="body1"
                        sx={{ mb: 4, mt: 4 }}>
                        <strong>Name</strong><Typography>{profileInfo?.name} {profileInfo?.surname}</Typography> 
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4 }}>
                        <strong>Sentinel Number</strong>
                        <Typography color={"orangered"} sx={{ fontWeight: "bold" }}>
                            {profileInfo?.pts}
                        </Typography>
                    </Typography>
                    <Typography variant="body1">
                        <strong>Phone Number</strong><Typography>{profileInfo?.phoneNumber}</Typography>
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography
                        variant="body1"
                        sx={{ mb: 4, mt: 4 }}>
                        <strong>Email</strong><Typography>{profileInfo?.email}</Typography> 
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        <strong>Post Code</strong><Typography>{profileInfo?.postCode}</Typography>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        <strong>Location</strong><Typography>{profileInfo?.location}</Typography> 
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography
                        variant="body1"
                        sx={{ mb: 4, mt: 4 }}>
                        <strong>Roles</strong>
                            <Typography>
                                {profileInfo?.roles?.map((role: any) => (
                                    <li>
                                        {role.roleName}
                                    </li>
                                ))}
                            </Typography>
                    </Typography>
                </Grid>
            </Box>
            <Modal
                title={`Delete This User`}
                onClose={() => setOpen(false)}
                open={open}
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", md: "1fr"},
                            gap: 3
                        }}>
                        <Typography sx={{ fontWeight: "600"}}>Delete {profileInfo?.name} {profileInfo?.surname}</Typography>
                        <Typography sx={{mb: -3}}>To confirm, type "{profileInfo?.username}" in the box below</Typography>
                        <TextField
                            type="text"
                            size="small"
                            value={username}
                            onChange={(e) => {setUsername(e.target.value)}}
                        />
                        <div className="center-button-div">
                            <Button variant="contained" onClick={handleDeleteUser} style={{boxShadow: 'none', width: 200, backgroundColor: 'red'}}>
                                Delete this user
                            </Button>
                        </div>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}

export default ProfileAdmin;
